import { ChevronRight, Eye, Settings01, Users01, XClose } from "@untitledui/icons";
import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import AccessibilityPanel from "@/features/AccessibilityPanel/AccessibilityPanel";
import { useDeviceType } from "@/hooks/useDeviceType";
import { postUserRoleSwitch } from "@/services/postLoginUser";
import { ROLE_NAME_MAP } from "@/shared/utils/maps";
import type { AppDispatch } from "@/store";
import { selectCommonConfig } from "@/store/adminSlice";
import type { ThemeName } from "@/store/theme";
import { selectTheme, setThemeAndPersist } from "@/store/theme";
import {
  selectIsAdmin,
  selectRoles,
  selectSelectedRole,
  selectAccessibilityOpen,
  setAccessibilityOpen,
  setSelectedRole,
  selectIsOperator,
} from "@/store/userSlice";

import { getInitials } from "../utils/profile";
import ProfileModal from "./ProfileModal";

interface ProfileMenuProps {
  displayName: string;
  email?: string;
  onLogout: () => void;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({ displayName, email, onLogout }) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useSelector(selectTheme) as ThemeName;
  const { t } = useTranslation();

  const roles = useSelector(selectRoles) as string[];
  const selectedRole = useSelector(selectSelectedRole);
  const isAdmin = useSelector(selectIsAdmin);
  const isOperator = useSelector(selectIsOperator);
  const accessibilityOpen = useSelector(selectAccessibilityOpen);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const { isMobile } = useDeviceType();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  const config = useSelector(selectCommonConfig);

  const themeOptions = [
    config?.VibrantTheme
      ? {
          key: "theme1" as ThemeName,
          label: t("themes.vibrant", { defaultValue: "Vibrant Theme" }),
        }
      : null,
    config?.LightTheme
      ? {
          key: "theme2" as ThemeName,
          label: t("themes.light", { defaultValue: "Light Theme" }),
        }
      : null,
    config?.DarkTheme
      ? {
          key: "theme3" as ThemeName,
          label: t("themes.dark", { defaultValue: "Dark Theme" }),
        }
      : null,
  ].filter((item): item is { key: ThemeName; label: string } => item !== null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
        setThemeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const openProfile = () => {
    setOpen(false);
    setProfile({
      displayName,
      email,
      organization: "Mastercard Foundation",
      role: selectedRole ? ROLE_NAME_MAP[selectedRole] : undefined,
    });
    setProfileOpen(true);
  };

const switchRole = async (role: string) => {
  dispatch(setSelectedRole(role));
  setOpen(false);

  try {
    if (email) {
      await postUserRoleSwitch(
        ROLE_NAME_MAP[role] || role
      );
    }
  } catch (err) {
    console.error("Role switch logging failed:", err);
  }

  if (role === "Admin" || role === "Operator") {
    navigate("/admin-portal");
  } else {
    navigate("/dashboard");
  }
};

  return (
    <>
      {!isMobile && (
        <>
          {accessibilityOpen && (
            <AccessibilityPanel
              open
              onClose={() => dispatch(setAccessibilityOpen(false))}
            />
          )}
          <ProfileModal
            open={profileOpen}
            onClose={() => setProfileOpen(false)}
            profile={profile}
          />
        </>
      )}

      <div ref={containerRef} className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 px-1 py-1 rounded-md hover:bg-[var(--sidebar-hover)]"
        >
          <span className="h-8 w-8 flex items-center justify-center rounded-full bg-[var(--color-brand)] text-white text-xs font-bold">
            {getInitials(displayName)}
          </span>

          <span className="hidden sm:inline text-sm font-medium text-[var(--text-primary)]">
            {displayName}
          </span>
        </button>

        {open && (
          <div
            id={menuId}
            className="absolute right-0 mt-2 w-[420px] max-w-[90vw] rounded-xl shadow-xl z-[1100] bg-[var(--bg-surface)] border border-[var(--border-color)] transition-colors duration-500 max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="px-4 py-4 border-b border-[var(--border-color)] flex items-center gap-3 shrink-0">
              <div className="h-10 w-10 rounded-full bg-[var(--color-brand)] text-white flex items-center justify-center font-semibold">
                {getInitials(displayName)}
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--text-primary)]">{displayName}</div>
                {email && <div className="text-xs text-gray-500">{email}</div>}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain min-h-0">
              {(roles.length > 1 || isAdmin || isOperator) && (
                <div className="px-4 py-3 border-b border-[var(--border-color)]">
                  <div
                    className="text-xs text-gray-500 mb-2 uppercase"
                    aria-label={t("viewApplication", { defaultValue: "View Application As" })}
                  >
                    {t("viewApplication", {
                      defaultValue: "View Application As",
                    })}
                  </div>

                  {roles.map((role) => {
                    const active = role === selectedRole;

                    return (
                      <button
                        key={role}
                        onClick={() => switchRole(role)}
                        className={`flex w-full items-center justify-between px-3 py-2 rounded-md text-sm ${
                          active ? "bg-[var(--select-bg)]" : "hover:bg-[var(--select-bg)]"
                        }`}
                      >
                        <span className="text-[var(--text-primary)]">
                          {ROLE_NAME_MAP[role] || role}
                        </span>

                        <span
                          className="h-4 w-4 rounded-full border flex items-center justify-center"
                          style={{
                            borderColor: active ? "var(--color-brand)" : "var(--border-color)",
                          }}
                        >
                          {active && (
                            <span
                              className="h-2 w-2 rounded-full"
                              style={{ background: "var(--color-brand)" }}
                            />
                          )}
                        </span>
                      </button>
                    );
                  })}
                  {isAdmin && (
                    <button
                     onClick={() => switchRole("Admin")}
                      className={`flex w-full items-center justify-between px-3 py-2 rounded-md text-sm ${
                        selectedRole === "Admin"
                          ? "bg-[var(--select-bg)]"
                          : "hover:bg-[var(--select-bg)]"
                      }`}
                    >
                      <span className="text-[var(--text-primary)]">Admin</span>

                      <span
                        className="h-4 w-4 rounded-full border flex items-center justify-center"
                        style={{
                          borderColor:
                            selectedRole === "Admin" ? "var(--color-brand)" : "var(--border-color)",
                        }}
                      >
                        {selectedRole === "Admin" && (
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ background: "var(--color-brand)" }}
                          />
                        )}
                      </span>
                    </button>
                  )}
                   {isOperator && (
                    <button
                     onClick={() => switchRole("Operator")}
                      className={`flex w-full items-center justify-between px-3 py-2 rounded-md text-sm ${
                        selectedRole === "Operator"
                          ? "bg-[var(--select-bg)]"
                          : "hover:bg-[var(--select-bg)]" 
                      }`}
                    >
                      <span className="text-[var(--text-primary)]">Operator</span>

                      <span
                        className="h-4 w-4 rounded-full border flex items-center justify-center"
                        style={{
                          borderColor:
                            selectedRole === "Operator" ? "var(--color-brand)" : "var(--border-color)",
                        }}
                      >
                        {selectedRole === "Operator" && (
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ background: "var(--color-brand)" }}
                          />
                        )}
                      </span>
                    </button>
                  )}
                </div>
              )}

              <div className="py-2 border-b border-[var(--border-color)]">
                <button
                  onClick={openProfile}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--select-bg)]"
                >
                  <Users01 className="h-4 w-4" />
                  {t("viewProfile.title", { defaultValue: "View Profile" })}
                </button>

                <div>
                  <button
                    onClick={() => setThemeOpen((v) => !v)}
                    className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--select-bg)]"
                  >
                    <Eye className="h-4 w-4" />
                    {t("themes.title", { defaultValue: "Themes" })}
                    <ChevronRight
                      className={`ml-auto h-4 w-4 text-gray-400 transition-transform ${
                        themeOpen ? "rotate-90" : ""
                      }`}
                    />
                  </button>

                  {themeOpen && (
                    <div className="ml-4">
                      {themeOptions.map((opt) => {
                        const selected = theme === opt.key;

                        return (
                          <button
                            key={opt.key}
                            onClick={() => dispatch(setThemeAndPersist(opt.key))}
                            className={`flex w-full items-center justify-between px-3 py-2 text-sm ${
                              selected ? "bg-[var(--select-bg)]" : "hover:bg-[var(--select-bg)]"
                            }`}
                          >
                            <span className="text-[var(--text-primary)]">{opt.label}</span>

                            <span
                              className="h-4 w-4 rounded-full border flex items-center justify-center"
                              style={{
                                borderColor: selected
                                  ? "var(--color-brand)"
                                  : "var(--border-color)",
                              }}
                            >
                              {selected && (
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={{ background: "var(--color-brand)" }}
                                />
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    setOpen(false);
                    dispatch(setAccessibilityOpen(true));
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--select-bg)]"
                >
                  <Settings01 className="h-4 w-4" />
                  {t("accessibilityTitle", {
                    defaultValue: "Accessibility Settings",
                  })}
                </button>
              </div>

              <div className="py-2">
                <button
                  onClick={() => {
                    setOpen(false);
                    onLogout();
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2 text-sm text-[var(--color-brand)] hover:bg-[var(--select-bg)]"
                >
                  <XClose className="h-4 w-4" />
                  {t("signOut", { defaultValue: "Sign Out" })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ProfileMenu;
